import type { ShipVariantOption } from "@/types/missions"
import type { ShipmentProduct } from "@/types/shipments"

/**
 * OKS-989/OKS-995 — what may be recorded as shipped for one row.
 *
 * ⚠️ NOT `product.variant.variants`. That is the row's display SUMMARY and is
 * EMPTY by design for `open_choice`, so reading it here would leave the select
 * absent for exactly the unpinned multi-variant missions the control exists to
 * serve — while the row above the dialog still read correctly, and with nothing
 * on either side comparing the two.
 *
 * ⚠️ ONE definition, TWO pages. The Shipments queue and the mission panel are
 * the two callers of one dialog. OKS-995 brought the panel onto the per-row
 * payload the queue already used; extracting this is what stops the two
 * growing separate answers to "what may this row ship". A second copy is how
 * one parcel comes to be described two ways on two surfaces.
 *
 * A row with no product — a productless drop, a promotional mission, or a
 * redemption, which has no variant table at all — yields `[]`.
 */
export function shippableVariantsFor(
  product: ShipmentProduct | null | undefined,
): ShipVariantOption[] {
  return product?.shippable_variants ?? []
}

/**
 * OKS-1028 D1 (OQ-A) — does `edit` mode offer the correction control?
 *
 *   - ≥2 offered variants: yes (OKS-996 D1).
 *   - exactly 1: yes UNLESS the row records that very variant. Nothing
 *     recorded (the R8b re-pin case), a record naming a variant that was
 *     un-pinned since, and a record whose variant was hard-deleted all offer
 *     it: in each the single option is a genuine correction, not a restatement.
 *     Only "records W" leaves the recorded line to state a settled fact.
 *   - 0: no.
 *
 * ⚠️ IDENTITY, NEVER THE LABEL. "Records W" compares the recorded variant's id
 * with the option's id. Two variants can share a title, and a hard-deleted
 * variant (FK nullified, `on_delete: :nullify`) keeps a label that may read
 * exactly like W's — a label comparison hides the control in exactly that
 * case. A renamed W keeps its id, so it stays "records W": a rename is not a
 * correction.
 *
 * ⚠️ A caller that DROPS the id (it is optional in the row types, so `tsc`
 * cannot see it) turns every "records W" row into a control over a settled
 * record. Not destructive — the select opens on « keep » — but wrong; the
 * queue adapter `toDialogRow` is where OKS-987 once dropped the label, and its
 * mount arm (`correction-rule-mount-oks1028.test.tsx`) reds on the drop.
 * `undefined` reads like `null`: not that variant.
 */
export function correctionOffered(
  options: readonly ShipVariantOption[],
  recordedVariantId: number | null | undefined,
): boolean {
  if (options.length >= 2) return true
  return options.length === 1 && options[0].id !== recordedVariantId
}
